// MailRolloutCard - super admin only. Which mail changes are switched on
// for which company (owner brief 2026-10-11 T33): a change to who receives
// an email, or to who sees hours and cost, ships OFF for every company and
// is switched on here once that company's admins have been told.
//
// Data: GET /notification-settings/rollout               → { keys, companies }
//       PUT /notification-settings/rollout/:companyId    { keys } → { companyId, keys }
//
// The screen renders from what the server stored, never from what it sent.
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Card, Checkbox, Input, Skeleton, message } from "antd";
import { url, tokenRef } from "api";
import { T } from "pages/Issue/theme";

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` };
}

export default function MailRolloutCard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState({}); // companyId -> bool

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await fetch(`${url}/notification-settings/rollout`, { headers: auth() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.err || `HTTP ${res.status}`);
      setData(body);
    } catch (e) {
      setError(e.message || "Could not load the rollout switches");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (companyId, keys) => {
    setSaving((s) => ({ ...s, [companyId]: true }));
    try {
      const res = await fetch(`${url}/notification-settings/rollout/${companyId}`, {
        method: "PUT",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ keys }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.err || `HTTP ${res.status}`);
      setData((d) => ({
        ...d,
        companies: d.companies.map((c) => (c.companyId === companyId ? { ...c, keys: body.keys || [] } : c)),
      }));
    } catch (e) {
      message.error(e.message || "Could not save");
    } finally {
      setSaving((s) => ({ ...s, [companyId]: false }));
    }
  };

  const q = filter.trim().toLowerCase();
  const companies = (data?.companies || [])
    .filter((c) => !q || String(c.name).toLowerCase().includes(q))
    // Companies with something on first, so what is live is never scrolled away.
    .sort((a, b) => (b.keys.length > 0) - (a.keys.length > 0) || String(a.name).localeCompare(String(b.name)));

  return (
    <Card style={{ borderColor: T.line, marginBottom: 16 }} size="small"
          title={<span style={{ fontWeight: 700, color: T.ink }}>Mail changes switched on per company (super admin)</span>}
          extra={<Input size="small" allowClear placeholder="Find a company" style={{ width: 200 }}
                        value={filter} onChange={(e) => setFilter(e.target.value)} />}>
      <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 10 }}>
        Each change to who receives an email, or to who sees hours and cost, is off for every company until it is ticked here.
        Tell that company's admins what changes and when before ticking. Unticked, the company keeps today's behaviour.
        Changes save immediately.
      </div>
      {error ? <Alert type="error" showIcon message={error} />
        : !data ? <Skeleton active paragraph={{ rows: 3 }} />
        : (
          <div style={{ maxHeight: 420, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            {companies.length === 0 && <div style={{ color: T.muted, fontSize: 12.5 }}>No company matches.</div>}
            {companies.map((c) => {
              const all = c.keys.includes("*");
              return (
                <div key={c.companyId} style={{ border: `1px solid ${T.line}`, borderRadius: 6, padding: "6px 10px" }}>
                  <div style={{ fontWeight: 700, color: T.ink, fontSize: 13, marginBottom: 4 }}>
                    {c.name}
                    {all && <span style={{ color: T.muted, fontWeight: 400 }}> - every change is on (*)</span>}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px" }}>
                    {data.keys.map((k) => (
                      <Checkbox key={k.key} checked={all || c.keys.includes(k.key)} disabled={all || !!saving[c.companyId]}
                                onChange={(e) => save(c.companyId, e.target.checked
                                  ? [...new Set([...c.keys, k.key])]
                                  : c.keys.filter((x) => x !== k.key))}>
                        <span style={{ fontSize: 12.5 }}>{k.label}</span>
                      </Checkbox>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </Card>
  );
}
